import { Box, Button, CardContent, CircularProgress, Collapse, IconButton, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import BaseWrapper from "../../../components/BaseWrapper/BaseWrapper";
import { CloudDownload, ExpandLess, ExpandMore } from "@mui/icons-material";
import ReactECharts from 'echarts-for-react';
import { API } from "../../../lib/api";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import weekOfYear from "dayjs/plugin/weekOfYear";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { useEffect, useMemo, useRef, useState } from "react";
import QuoteColumns from "../../../lib/TableColumns/QuoteColumns"
import DailyQuoteTrendCard from "../../../components/DailyQuoteTrendCard";

dayjs.extend(isoWeek);
dayjs.extend(weekOfYear);

const formatUSD = (n = 0) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
const getCurrentWeekRange = () => ({
    start: dayjs().startOf("isoWeek"),
    end: dayjs().endOf("isoWeek"),
});

const WeeklyQuotesReport = () => {
    const [loading, setLoading] = useState(true);
    const [quotes,setQuotes] = useState([])
    const [collapsedWeekly, setCollapseWeekly] = useState(false)
    const [defaultStartDate, setDefaultStartDate] = useState(() =>
        getCurrentWeekRange().start.format("YYYY-MM-DD")
    );
    const [defaultEndDate, setDefaultEndDate] = useState(() =>
        getCurrentWeekRange().end.format("YYYY-MM-DD")
    );
    const chartRef = useRef();
    const columns = QuoteColumns()
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    
    const { labels, values, year } = useMemo(() => {
        const year = dayjs().year();


        const endOfYear = dayjs().year(year).endOf("year");

        // How many ISO weeks are in this year? (52 or 53)
        const weeksInYear = endOfYear.isoWeek();

        const weeklyTotals = Array.from({ length: weeksInYear }, () => 0);

        for (const q of quotes) {
        if (!q?.createdAt) continue;

        const created = dayjs(q.createdAt);
        if (created.year() !== year) continue;

        const weekIndex = created.isoWeek() - 1; // 0-based index
        if (weekIndex < 0 || weekIndex >= weeklyTotals.length) continue;

        const quoteTotal = (q.lines || [])
            .filter((l) => {
                const type = l.type || l.rowType;
                return type !== "subtotal" && type !== "runningsubtotal";
            })
            .reduce((sum, l) => sum + (Number(l.extPrice) || 0), 0);

        weeklyTotals[weekIndex] += quoteTotal;
        }

        const labels = Array.from({ length: weeksInYear }, (_, i) => `W${i + 1}`);

        return { labels, values: weeklyTotals, year };
    }, [quotes]);

    const mobileZoomRange = useMemo(() => {
        const totalWeeks = labels.length || 1;
        const currentWeekIndex = Math.max(0, Math.min(totalWeeks - 1, dayjs().isoWeek() - 1));
        const startIndex = Math.max(0, currentWeekIndex - 5);
        const endIndex = currentWeekIndex;

        return {
            start: (startIndex / totalWeeks) * 100,
            end: ((endIndex + 1) / totalWeeks) * 100,
        };
    }, [labels.length]);

    const option = useMemo(
        () => ({
        title: { text: `Weekly Quote Total (${year})`, left: "center" },
        tooltip: {
            trigger: "axis",
            valueFormatter: (val) => formatUSD(val),
        },
        grid: {
            left: isMobile ? 44 : 60,
            right: isMobile ? 12 : 20,
            top: isMobile ? 64 : 60,
            bottom: isMobile ? 92 : 60,
            containLabel: true
        },
        xAxis: {
            type: "category",
            data: labels,
            axisLabel: {
                interval: isMobile ? 0 : 1,
                rotate: isMobile ? 35 : 0,
                fontSize: isMobile ? 10 : 12
            },
        },
        yAxis: {
            type: "value",
            axisLabel: {
            formatter: (val) => formatUSD(val),
            fontSize: isMobile ? 10 : 12,
            },
        },
        dataZoom: [
            {
                type: "inside",
                start: isMobile ? mobileZoomRange.start : 0,
                end: isMobile ? mobileZoomRange.end : 100,
            },
            {
                type: "slider",
                start: isMobile ? mobileZoomRange.start : 0,
                end: isMobile ? mobileZoomRange.end : 100,
                height: isMobile ? 18 : 24
            },
        ],
        series: [
            {
            name: "Total",
            type: "bar",
            data: values,
            barMaxWidth: isMobile ? 16 : 22,
            },
        ],
        }),
        [isMobile, labels, mobileZoomRange.end, mobileZoomRange.start, values, year]
    );    

    const handlePDF = async () => {
        setLoading(true)
        const res = await API.Quotes.DeliveryWeeklyReport(
            dayjs(defaultStartDate).startOf("day").toDate(),
            dayjs(defaultEndDate).endOf("day").toDate()
        );
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = `Weekly Quotes Report.pdf`
        document.body.appendChild(a);
        a.click();
        a.remove();

        window.URL.revokeObjectURL(url);
        setLoading(false)
    };     

    const handleResetToCurrentWeek = () => {
        const currentWeek = getCurrentWeekRange();
        setDefaultStartDate(currentWeek.start.format("YYYY-MM-DD"));
        setDefaultEndDate(currentWeek.end.format("YYYY-MM-DD"));
    };

    const weeklyQuotes = useMemo(() => {
        const start = dayjs(defaultStartDate).startOf("day");
        const end = dayjs(defaultEndDate).endOf("day");

        return quotes.filter((q) => {
            if (!q?.createdAt) return false;

            const createdAt = dayjs(q.createdAt);
            if (!createdAt.isValid()) return false;
            return createdAt.valueOf() >= start.valueOf() && createdAt.valueOf() <= end.valueOf();
        });
    }, [quotes, defaultStartDate, defaultEndDate]);

    const total = useMemo(() => {
        return weeklyQuotes.reduce((quoteSum, q) => {
            const linesTotal = (q.lines || [])
                .filter((l) => l.type !== "subtotal" && l.type !== "runningsubtotal")
                .reduce((sum, line) => sum + (Number(line.extPrice) || 0), 0);

            return quoteSum + linesTotal;
        }, 0);
    }, [weeklyQuotes]);

    useEffect(() => {
        async function fetchData() {
            const quotesResponse = await API.Quotes.FetchAll()
            setQuotes(quotesResponse)
            setLoading(false)
        }

        fetchData()
    },[])

    return (
        <BaseWrapper
            content={
            <>
                {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <div className="col-xl-12 col-md-12" style={{ marginTop: "1em", marginBottom: "1em" }}>
                        <div className='card'>
                            <div
                                className="card-header d-flex align-items-center justify-content-between"
                                style={{ flexWrap: isMobile ? "wrap" : "nowrap", rowGap: isMobile ? "0.75rem" : 0 }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: { xs: "space-between", md: "flex-start" },
                                        width: { xs: "100%", md: "auto" },
                                        minWidth: 0
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, pr: 1 }}>
                                        <IconButton
                                            onClick={() => setCollapseWeekly(!collapsedWeekly)}
                                            size="small"
                                            sx={{ mr: 1 }}
                                        >
                                            {collapsedWeekly ? <ExpandMore /> : <ExpandLess />}
                                        </IconButton>
                                        <h5 style={{ margin: 0, lineHeight: 1.2 }}>
                                            Application Engineering - <span style={{ whiteSpace: "nowrap" }}>Quotes By Week</span>
                                        </h5>
                                    </Box>
                                </Box>
                                <Stack
                                    direction={{ xs: "column", sm: "row" }}
                                    spacing={1}
                                    sx={{ mt: 0, width: isMobile ? "100%" : "auto", ml: isMobile ? 0 : 4 }}
                                >
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DatePicker
                                            label="Start Date"
                                            value={dayjs(defaultStartDate)}
                                            format="MMM D, YYYY"
                                            onChange={(date) => {
                                                if (date && dayjs(date).isValid()) {
                                                    setDefaultStartDate(dayjs(date).format("YYYY-MM-DD"));
                                                }
                                            }}
                                            slotProps={{
                                                textField: {
                                                    size: "small",
                                                    sx: {
                                                        minWidth: { xs: "100%", sm: 180 },
                                                        "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: "#fff" },
                                                    },
                                                },
                                            }}
                                        />
                                        <DatePicker
                                            label="End Date"
                                            value={dayjs(defaultEndDate)}
                                            format="MMM D, YYYY"
                                            onChange={(date) => {
                                                if (date && dayjs(date).isValid()) {
                                                    setDefaultEndDate(dayjs(date).format("YYYY-MM-DD"));
                                                }
                                            }}
                                            slotProps={{
                                                textField: {
                                                    size: "small",
                                                    sx: {
                                                        minWidth: { xs: "100%", sm: 180 },
                                                        "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: "#fff" },
                                                    },
                                                },
                                            }}
                                        />
                                    </LocalizationProvider>
                                    <Button variant="outlined" onClick={handleResetToCurrentWeek}>
                                        Reset to Current Week
                                    </Button>
                                    <Button variant="outlined" onClick={handlePDF} startIcon={<CloudDownload />}>
                                        Download Report
                                    </Button>
                                </Stack>
                            </div>
                            <Collapse in={!collapsedWeekly} timeout="auto" unmountOnExit>
                                <CardContent>
                                    <ReactECharts theme="v5"
                                        ref={chartRef}
                                        option={option}
                                        style={{ height: isMobile ? "380px" : "500px", width: "100%" }}
                                        opts={{ renderer: 'svg' }}
                                    />

                                    <Box sx={{ display: "flex", justifyContent: "center", px: 1, mt: 2, mb: 2 }}>
                                        <Box sx={{ width: { xs: "100%", sm: "auto" }, textAlign: "center" }}>
                                            <Typography
                                                variant={isMobile ? "h6" : "h5"}
                                                sx={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
                                            >
                                                Weekly Total: {formatUSD(total)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <DataGridPro
                                        sx={{ backgroundColor: "white" }}
                                        rows={weeklyQuotes}
                                        columns={columns}
                                        getRowId={(row) => row._id}
                                        pagination
                                        initialState={{
                                            pagination: {
                                                paginationModel: { pageSize: 15, page: 0 },
                                            },
                                        }}
                                        pageSizeOptions={[15, 25, 50]}
                                    />
                                </CardContent>
                            </Collapse>
                        </div>
                    </div>
                    <DailyQuoteTrendCard quotes={quotes} />
                </>                  
            )}
            </>                              
            }
        />
    )
}

export default WeeklyQuotesReport

